<template>
  <div class="call-bucket-prompt-banner">
    <div class="banner-content">
      <div class="banner-left">
        <div class="call-icon">
          <Icon icon="mdi:phone-in-talk" width="16" height="16" />
        </div>
        <div class="banner-text">
          <h6 class="banner-title">Call with {{ contactName }}</h6>
          <p class="banner-details">Duration: {{ formatDuration(duration) }}</p>
        </div>
      </div>
      <div class="banner-right">
        <Select
          v-model="selectedBucketOptionId"
          placeholder="Select bucket"
          :options="bucketSelectOptions"
          size="sm"
          class="bucket-select"
        />
        <Button
          @click="handleCreateTask"
          :disabled="!selectedBucketOptionId"
          size="sm"
          variant="primary"
        >
          Create Task
        </Button>
        <button class="btn-close" @click="handleClose" aria-label="Close">
          <Icon icon="mdi:close" width="16" height="16" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useToast } from "@/composables/ui/useToast";
import { useStore } from "vuex";
import { Icon, Select, Button } from "@/components/ui";
import { useBucketOptions } from "@/composables/bucket";
const ONE_HOUR_MS = 60 * 60 * 1000;

const props = defineProps({
  callLogId: {
    type: String,
    required: true,
  },
  contactId: {
    type: String,
    required: true,
  },
  contactName: {
    type: String,
    required: true,
  },
  duration: {
    type: Number,
    default: null,
  },
  message: {
    type: String,
    default: "",
  },
  timestamp: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(["close", "create-task"]);

const $toast = useToast();

// Store
const store = useStore();
const account = computed(() => store.getters.account?.data);
const organizationId = computed(() => account.value?.organizationId);

// State
const selectedBucketOptionId = ref(null);

// Bucket options query
const bucketOptionsParams = computed(() => ({
  organizationId: organizationId.value,
}));
const { data: bucketOptionsData } = useBucketOptions(bucketOptionsParams);

const bucketOptions = computed(() => bucketOptionsData.value || []);

const bucketSelectOptions = computed(() => {
  return bucketOptions.value
    .filter((option) => option.isActive && !option.isDeleted)
    .map((option) => ({
      value: option.id,
      label: option.name,
      icon: "fa6-solid:bucket",
      iconColor: option.colorHex,
    }));
});

// Format duration helper
const formatDuration = (durationSeconds) => {
  if (durationSeconds === null || durationSeconds === undefined) {
    return "N/A";
  }
  if (durationSeconds < 60) {
    return `${durationSeconds} second${durationSeconds !== 1 ? "s" : ""}`;
  }
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  if (seconds === 0) {
    return `${minutes} min`;
  }
  return `${minutes} min ${seconds} sec`;
};

const handleClose = () => {
  emit("close");
};

const handleCreateTask = () => {
  if (!selectedBucketOptionId.value) return;
  emit("create-task", selectedBucketOptionId.value);
};

let autoDismissTimer = null;

// Auto-dismiss after 1 hour
onMounted(() => {
  const promptTime = props.timestamp || Date.now();
  const elapsed = Date.now() - promptTime;
  const remainingTime = ONE_HOUR_MS - elapsed;

  // If already expired, close immediately
  if (remainingTime <= 0) {
    handleClose();
    return;
  }

  // Set timeout for remaining time
  autoDismissTimer = setTimeout(() => {
    handleClose();
  }, remainingTime);
});

onUnmounted(() => {
  if (autoDismissTimer) {
    clearTimeout(autoDismissTimer);
    autoDismissTimer = null;
  }
});
</script>

<style scoped>
.call-bucket-prompt-banner {
  position: relative;
  width: 100%;
  background: #dbeafe;
  border-top: 1px solid #60a5fa;
  border-bottom: 1px solid #60a5fa;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 0;
}

.banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  max-width: 100%;
  margin: 0;
  gap: 12px;
}

.banner-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.call-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #3b82f6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.3);
}

.banner-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.banner-title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1e40af;
  line-height: 1.3;
}

.banner-details {
  margin: 0;
  font-size: 0.75rem;
  color: #374151;
  line-height: 1.4;
}

.banner-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.bucket-select {
  min-width: 150px;
}

.btn-close {
  background: transparent;
  border: none;
  color: #374151;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  opacity: 1;
}

.btn-close:hover {
  background-color: rgba(30, 64, 175, 0.1);
  opacity: 1;
}

@media (max-width: 768px) {
  .banner-content {
    flex-direction: column;
    align-items: flex-start;
    padding: 8px 12px;
  }

  .banner-right {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .bucket-select {
    min-width: 120px;
    flex: 1;
  }
}
</style>
